import { z } from 'zod';

export const KorchDispatchPolicy = z.object({
  idempotencyKeyExtractor: z.enum(['payload.id', 'payload.shipmentId', 'custom']),
  retryPolicy: z.object({
    maxRetries: z.number().min(0).max(10).default(3),
    backoffMs: z.array(z.number()).default([1000, 5000, 30000]),
  }),
  deadLetter: z.object({
    enabled: z.boolean().default(true),
    afterRetries: z.number().default(3),
  }),
  fanOutLimitPerTenantPerMin: z.number().default(100),
});

export const KorchDispatchAuditEntry = z.object({
  id: z.string(),                             // ULID
  tenantId: z.string(),
  eventId: z.string(),
  eventType: z.string(),
  policyApplied: z.string(),                  // policy version ref
  outcome: z.enum([
    'delivered', 'retried', 'dead-lettered', 'quota-exceeded', 'idempotent-skip',
  ]),
  attemptNumber: z.number(),
  correlationId: z.string(),
  timestamp: z.string(),
});

// ── KORCH-DOCTRINE-IMPL-1: composition substrate types ──────────────────────

export const KorchDeploymentMode = z.enum(['3PL', '5PL', '4PL', 'MT', 'IT']);

export const KorchCapabilitySet = z.object({
  substrate: z.string(),
  deploymentMode: KorchDeploymentMode,
  capabilities: z.array(z.string()),
  kscanSubtracks: z.array(z.string()),        // Track B subtracks in-scope for this mode
  contractVersion: z.string(),
});

export const KorchComposeResult = z.object({
  substrate: z.string(),
  deploymentMode: KorchDeploymentMode,
  surfaceContextId: z.string(),
  actionType: z.string(),
  outcome: z.enum(['success', 'partial', 'failed']),
  payload: z.record(z.unknown()),
  durationMs: z.number(),
  correlationId: z.string(),
});

// §5.2 · korch_telemetry_events schema (BINDING — patent claim anchor)
export const KorchTelemetryEvent = z.object({
  eventId: z.string(),                        // ULID
  deploymentMode: KorchDeploymentMode,
  surfaceContextId: z.string(),
  composedSubstrate: z.string(),              // 'KPOL'|'KWOL'|'KFOL'|...
  actionType: z.string(),
  intentId: z.string(),
  durationMs: z.number(),
  outcome: z.enum(['success', 'partial', 'failed']),
  payload: z.record(z.unknown()),
  emittedAt: z.string(),
  tenantId: z.string(),
  softDeleted: z.null(),                      // append-only invariant
});

// korch_compositions — lifecycle-tracked per surfaceContextId (§5.2)
export const KorchComposition = z.object({
  id: z.string(),                             // ULID
  surfaceContextId: z.string(),
  deploymentMode: KorchDeploymentMode,
  tenantId: z.string(),
  state: z.enum(['in-flight', 'completed', 'failed']),
  substratesCalled: z.array(z.string()),
  startedAt: z.string(),
  completedAt: z.string().nullable(),
  softDeleted: z.string().nullable(),
});

/** @typedef {import('zod').infer<typeof KorchDispatchPolicy>} KorchDispatchPolicyType */
/** @typedef {import('zod').infer<typeof KorchDispatchAuditEntry>} KorchDispatchAuditEntryType */
/** @typedef {import('zod').infer<typeof KorchDeploymentMode>} KorchDeploymentModeType */
/** @typedef {import('zod').infer<typeof KorchCapabilitySet>} KorchCapabilitySetType */
/** @typedef {import('zod').infer<typeof KorchComposeResult>} KorchComposeResultType */
/** @typedef {import('zod').infer<typeof KorchTelemetryEvent>} KorchTelemetryEventType */
/** @typedef {import('zod').infer<typeof KorchComposition>} KorchCompositionType */
